import React from "react";
import { useTranslation } from "../../hooks/useTranslation";

/**
 * Dinezi Terms & Conditions React component
 * - Single-file component using Tailwind CSS for styling
 * - Default export
 * - Dynamic current year
 * - Collapsible sections with anchors for quick navigation
 * - Lightweight and accessible (semantic HTML)
 *
 * Usage:
 * <DineziTermsAndConditions />
 */

export default function DineziTermsAndConditions() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const terms = t("termsAndConditions");

  const sections = [
    { id: "about", title: terms.sections.about.title },
    { id: "user-resp", title: terms.sections.userResponsibilities.title },
    { id: "bookings", title: terms.sections.tableBookings.title },
    { id: "orders", title: terms.sections.restaurantOrders.title },
    { id: "payments", title: terms.sections.payments.title },
    { id: "discounts", title: terms.sections.discounts.title },
    { id: "api", title: terms.sections.apiUsage.title },
    { id: "location", title: terms.sections.location.title },
    { id: "refunds", title: terms.sections.refunds.title },
    { id: "liability", title: terms.sections.liability.title },
    { id: "changes", title: terms.sections.changes.title },
    { id: "contact", title: terms.sections.contact.title },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="container max-w-6xl mx-auto px-4 py-6 sm:px-6 sm:py-10">
        <div className="max-w-4xl mx-auto bg-white rounded-xl sm:rounded-2xl shadow p-4 sm:p-6 md:p-10">
      <header className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold">{terms.title}</h1>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">{terms.lastUpdated.replace("{{year}}", year)}</p>
      </header>

      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
        {/* Sidebar / TOC */}
        <nav className="w-full lg:w-72 lg:sticky lg:top-6 self-start order-2 lg:order-1">
          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg border">
            <h2 className="text-xs sm:text-sm font-medium mb-2">{terms.contents}</h2>
            <ul className="text-sm space-y-1">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    className="text-blue-600 hover:underline"
                    href={`#${s.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById(s.id);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>

            <div className="mt-4 text-xs text-gray-500">
              <p dangerouslySetInnerHTML={{ __html: terms.copyright.replace("{{year}}", year) }} />
            </div>
          </div>
        </nav>

        {/* Main content */}
        <main className="prose prose-sm sm:prose-base max-w-none flex-1 order-1 lg:order-2">
          <p className="text-sm sm:text-base" dangerouslySetInnerHTML={{ __html: terms.welcome }} />

          <section id="about" className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{terms.sections.about.title}</h3>
            <p className="text-sm sm:text-base" dangerouslySetInnerHTML={{ __html: terms.sections.about.content }} />
          </section>

          <section id="user-resp" className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{terms.sections.userResponsibilities.title}</h3>
            <ul>
              <li>{terms.sections.userResponsibilities.items.accurateInfo}</li>
              <li>{terms.sections.userResponsibilities.items.lawfulUse}</li>
              <li>{terms.sections.userResponsibilities.items.noBypass}</li>
            </ul>
          </section>

          <section id="bookings" className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{terms.sections.tableBookings.title}</h3>
            <p className="text-sm sm:text-base mb-2 sm:mb-3">
              {terms.sections.tableBookings.intro}
            </p>
            <ol>
              <li dangerouslySetInnerHTML={{ __html: terms.sections.tableBookings.modes.menuOnly }} />
              <li dangerouslySetInnerHTML={{ __html: terms.sections.tableBookings.modes.bookingEnabled }} />
            </ol>

            <p className="text-sm sm:text-base mb-2 sm:mb-3">
              {terms.sections.tableBookings.afterRequest}
            </p>

            <details className="mb-2 sm:mb-3">
              <summary className="cursor-pointer text-sm sm:text-base">{terms.sections.tableBookings.tokenCharges.summary}</summary>
              <p className="text-sm sm:text-base mt-2" dangerouslySetInnerHTML={{ __html: terms.sections.tableBookings.tokenCharges.content }} />
            </details>

            <p className="text-sm sm:text-base" dangerouslySetInnerHTML={{ __html: terms.sections.tableBookings.ongoingBooking }} />
          </section>

          <section id="orders" className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{terms.sections.restaurantOrders.title}</h3>
            <p className="text-sm sm:text-base mb-2 sm:mb-3">{terms.sections.restaurantOrders.intro}</p>
            <ul>
              <li>{terms.sections.restaurantOrders.items.reservedTable}</li>
              <li>{terms.sections.restaurantOrders.items.directVisit}</li>
            </ul>

            <p className="text-sm sm:text-base mb-2 sm:mb-3">
              {terms.sections.restaurantOrders.tokenPayment}
            </p>

            <details className="mb-2 sm:mb-3">
              <summary className="cursor-pointer text-sm sm:text-base">{terms.sections.restaurantOrders.cartRules.summary}</summary>
              <ol>
                <li>{terms.sections.restaurantOrders.cartRules.items.verifyCart}</li>
                <li>{terms.sections.restaurantOrders.cartRules.items.noMixing}</li>
                <li>{terms.sections.restaurantOrders.cartRules.items.discounts}</li>
              </ol>
            </details>
          </section>

          <section id="payments" className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{terms.sections.payments.title}</h3>
            <p className="text-sm sm:text-base" dangerouslySetInnerHTML={{ __html: terms.sections.payments.content }} />
          </section>

          <section id="discounts" className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{terms.sections.discounts.title}</h3>
            <p className="text-sm sm:text-base">
              {terms.sections.discounts.content}
            </p>
          </section>

          <section id="api" className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{terms.sections.apiUsage.title}</h3>
            <p className="text-sm sm:text-base mb-2 sm:mb-3" dangerouslySetInnerHTML={{ __html: terms.sections.apiUsage.content }} />
            <blockquote className="border-l-2 pl-3 italic text-gray-600">
              {terms.sections.apiUsage.noRestaurants}
            </blockquote>
          </section>

          <section id="location" className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{terms.sections.location.title}</h3>
            <p className="text-sm sm:text-base">
              {terms.sections.location.content}
            </p>
          </section>

          <section id="refunds" className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{terms.sections.refunds.title}</h3>
            <p className="text-sm sm:text-base">
              {terms.sections.refunds.content}
            </p>
          </section>

          <section id="liability" className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{terms.sections.liability.title}</h3>
            <p className="text-sm sm:text-base">
              {terms.sections.liability.content}
            </p>
          </section>

          <section id="changes" className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{terms.sections.changes.title}</h3>
            <p className="text-sm sm:text-base">
              {terms.sections.changes.content}
            </p>
          </section>

          <section id="contact" className="mb-6 sm:mb-8">
            <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{terms.sections.contact.title}</h3>
            <p className="text-sm sm:text-base">
              {terms.sections.contact.content}
              <br />
              <a className="text-blue-600 hover:underline" href={`mailto:${terms.sections.contact.email}`}>
                {terms.sections.contact.email}
              </a>
            </p>
          </section>

          <hr />

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mt-4 sm:mt-6">
            <label className="flex items-start sm:items-center gap-2 text-xs sm:text-sm flex-1">
              <input type="checkbox" aria-label="accept-terms" className="w-4 h-4 sm:w-5 sm:h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 mt-0.5 sm:mt-0" />
              <span className="text-xs sm:text-sm">{terms.agreeCheckbox}</span>
            </label>

            <button
              className="w-full sm:w-auto sm:ml-auto inline-flex items-center justify-center gap-2 px-3 py-1.5 text-xs sm:text-sm rounded-md border hover:bg-gray-50"
              onClick={() => window.print()}
            >
              {terms.print}
            </button>
          </div>
        </main>
      </div>
        </div>
      </div>
    </div>
  );
}
